import { useState } from "react";

/**
 * LeagueBadge — Shows the SPECIFIC league logo based on sport + league name.
 *
 * Supported:
 *   NBA → NBA league logo (ESPN CDN)
 *   MLB → MLB league logo (ESPN CDN)
 *   Football + league name → Specific European league crest (football-data.org CDN)
 *   Football (unknown league) → collage of top EU leagues
 *
 * Props:
 *   sport  : "nba" | "mlb" | "football"
 *   league : league name string (e.g. "Champions League", "NBA", "Premier League")
 *   size   : "sm" | "md" | "lg"
 */

// ─── League logo URLs ────────────────────────────────────────────────────────
const LEAGUE_URLS = {
  // NBA & MLB — ESPN CDN (public, no API key)
  nba: "https://a.espncdn.com/i/teamlogos/leagues/500/nba.png",
  mlb: "https://a.espncdn.com/i/teamlogos/leagues/500/mlb.png",

  // European football — football-data.org CDN (same used for team crests)
  "champions league":   "https://crests.football-data.org/CL.png",
  "uefa champions league": "https://crests.football-data.org/CL.png",
  "championsleague":    "https://crests.football-data.org/CL.png",
  "premier league":     "https://crests.football-data.org/PL.png",
  "la liga":            "https://crests.football-data.org/PD.png",
  "primera division":   "https://crests.football-data.org/PD.png",
  "serie a":            "https://crests.football-data.org/SA.png",
  "bundesliga":         "https://crests.football-data.org/BL1.png",
  "ligue 1":            "https://crests.football-data.org/FL1.png",
  "eredivisie":         "https://crests.football-data.org/DED.png",
  "liga nos":           "https://crests.football-data.org/PPL.png",
  "europa league":      "https://crests.football-data.org/EL.png",
  "mls":                "https://crests.football-data.org/MLS.png",
};

// Collage of top EU leagues (for unknown football leagues)
const EU_COLLAGE = [
  { code: "CL",  label: "Champions" },
  { code: "PL",  label: "Premier"   },
  { code: "PD",  label: "La Liga"   },
  { code: "SA",  label: "Serie A"   },
  { code: "BL1", label: "Bundesliga"},
];

// Accent colors per sport
export const SPORT_ACCENT = {
  nba:      { color: "#F97316", bg: "rgba(249,115,22,0.08)",  border: "rgba(249,115,22,0.35)", glow: "rgba(249,115,22,0.15)" },
  mlb:      { color: "#3B82F6", bg: "rgba(59,130,246,0.08)",  border: "rgba(59,130,246,0.35)", glow: "rgba(59,130,246,0.1)" },
  football: { color: "#D4AF37", bg: "rgba(212,175,55,0.06)",  border: "rgba(212,175,55,0.25)", glow: "rgba(212,175,55,0.1)" },
};

// ─── Single collage mini-logo ─────────────────────────────────────────────────
function CollageLogo({ code, label, size }) {
  const [err, setErr] = useState(false);
  if (err) return null;
  return (
    <img
      src={`https://crests.football-data.org/${code}.png`}
      alt={label}
      title={label}
      style={{ width: size, height: size, objectFit: "contain", flexShrink: 0 }}
      onError={() => setErr(true)}
    />
  );
}

// ─── Main LeagueBadge ─────────────────────────────────────────────────────────
export default function LeagueBadge({ sport, league = "", size = "md" }) {
  const [imgErr, setImgErr] = useState(false);

  // Size mapping: logo image height
  const logoH = { sm: 22, md: 32, lg: 44 }[size] || 32;
  const collageItemSize = { sm: 16, md: 22, lg: 28 }[size] || 22;

  // Resolve league logo URL
  const leagueKey = league?.toLowerCase().trim() || "";
  const sportKey = sport?.toLowerCase() || "";

  let logoUrl = null;

  // NBA & MLB: use the sport key directly
  if (sportKey === "nba") logoUrl = LEAGUE_URLS.nba;
  else if (sportKey === "mlb") logoUrl = LEAGUE_URLS.mlb;
  // Football: try to find specific league logo
  else if (sportKey === "football") {
    logoUrl = LEAGUE_URLS[leagueKey] || null;
  }

  // ── NBA / MLB — show the league logo image ──────────────────────────────────
  if (sportKey === "nba" || sportKey === "mlb") {
    const accent = SPORT_ACCENT[sportKey];
    if (imgErr || !logoUrl) {
      // Fallback pill
      return (
        <span
          className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-[11px] font-black tracking-wider flex-shrink-0"
          style={{ background: accent.bg, color: accent.color, border: `1px solid ${accent.border}` }}
        >
          {sportKey === "nba" ? "NBA" : "MLB"}
        </span>
      );
    }
    return (
      <img
        src={logoUrl}
        alt={sportKey.toUpperCase()}
        title={sportKey.toUpperCase()}
        style={{
          height: logoH,
          width: "auto",
          objectFit: "contain",
          flexShrink: 0,
          filter: `drop-shadow(0 0 6px ${accent.glow})`,
        }}
        onError={() => setImgErr(true)}
      />
    );
  }

  // ── Football: specific league logo ─────────────────────────────────────────
  if (sportKey === "football") {
    if (logoUrl && !imgErr) {
      return (
        <div
          style={{
            background: "rgba(255,255,255,0.92)",
            borderRadius: "50%",
            padding: logoH > 30 ? 5 : 3,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 0 10px rgba(255,255,255,0.15)",
          }}
        >
          <img
            src={logoUrl}
            alt={league}
            title={league}
            style={{
              height: logoH,
              width: logoH,
              objectFit: "contain",
            }}
            onError={() => setImgErr(true)}
          />
        </div>
      );
    }

    // Unknown football league or image failed — collage
    return (
      <div
        className="inline-flex items-center flex-shrink-0"
        style={{ gap: size === "sm" ? 2 : 4 }}
        title="Fútbol Europeo"
      >
        {EU_COLLAGE.map((l) => (
          <div
            key={l.code}
            style={{
              background: "rgba(255,255,255,0.9)",
              borderRadius: "50%",
              padding: 2,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CollageLogo code={l.code} label={l.label} size={collageItemSize} />
          </div>
        ))}
      </div>
    );
  }

  // ── Default fallback ────────────────────────────────────────────────────────
  return null;
}
